import axios, { InternalAxiosRequestConfig } from 'axios';
import { User } from '../types/auth';

// TODO: Replace this with your new ngrok URL after restarting ngrok
const API_URL = process.env.REACT_APP_API_URL;

// Add debug logging
const debugLog = ( message: string, data?: any ) => {

};

const api = axios.create( {
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Client-Type': 'PostmanRuntime/7.44.1'
  }
} );

api.interceptors.request.use( ( config: InternalAxiosRequestConfig ) => {
  const token = localStorage.getItem( 'accessToken' );
  debugLog( 'Current access token:', token );

  if ( token ) {
    // Match exactly the working curl command format
    if ( config.headers ) {
      config.headers[ 'Authorization' ] = `Bearer ${ token.trim() }`;
      config.headers[ 'Accept' ] = 'application/json';
      config.headers[ 'X-Client-Type' ] = 'PostmanRuntime/7.44.1';
    }
    debugLog( 'Request headers after adding token:', config.headers );
  } else {
    debugLog( 'No access token found in localStorage' );
  }

  debugLog( 'Full request config:', {
    url: config.url,
    method: config.method,
    headers: config.headers,
    data: config.data
  } );

  return config;
} );

// Handle token refresh and 403 errors
api.interceptors.response.use(
  ( response ) => {
    debugLog( 'Response received:', {
      status: response.status,
      data: response.data,
      headers: response.headers
    } );
    return response;
  },
  async ( error ) => {
    debugLog( 'Response error:', {
      status: error.response?.status,
      message: error.message,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      }
    } );

    // Handle CORS errors
    if ( error.message === 'Network Error' || error.code === 'ERR_NETWORK' ) {
      console.error( 'CORS or Network Error:', error );
      // Don't redirect on CORS errors, just show a message
      alert( 'Connection error: Please check if the backend server is running and accessible' );
      return Promise.reject( error );
    }

    const originalRequest = error.config;

    // Handle 403 errors (Forbidden)
    if ( error.response?.status === 403 ) {
      debugLog( '403 error detected - Clearing tokens' );
      localStorage.removeItem( 'accessToken' );
      localStorage.removeItem( 'refreshToken' );
      window.location.href = '/login';
      return Promise.reject( error );
    }

    // If the error is 401 and we haven't tried to refresh the token yet
    if ( error.response?.status === 401 && !originalRequest._retry ) {
      debugLog( '401 error detected - Attempting token refresh' );
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem( 'refreshToken' );
        debugLog( 'Current refresh token:', refreshToken );

        if ( !refreshToken ) {
          throw new Error( 'No refresh token available' );
        }

        // Call refresh token endpoint
        debugLog( 'Calling refresh token endpoint' );
        const response = await axios.post( `${ API_URL }/admin/refresh-token`, {
          refreshToken,
        } );

        const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
        debugLog( 'Token refresh successful, new tokens received' );

        // Store new tokens
        localStorage.setItem( 'accessToken', accessToken );
        localStorage.setItem( 'refreshToken', newRefreshToken );
        debugLog( 'New tokens stored in localStorage' );

        // Update the original request with new token
        if ( originalRequest.headers ) {
          originalRequest.headers[ 'Authorization' ] = `Bearer ${ accessToken.trim() }`;
          originalRequest.headers[ 'X-Client-Type' ] = 'PostmanRuntime/7.44.1';
        }
        debugLog( 'Original request updated with new token' );

        // Retry the original request
        return api( originalRequest );
      } catch ( refreshError ) {
        debugLog( 'Token refresh failed:', refreshError );
        localStorage.removeItem( 'accessToken' );
        localStorage.removeItem( 'refreshToken' );
        window.location.href = '/login';
        return Promise.reject( refreshError );
      }
    }

    return Promise.reject( error );
  }
);

export const userService = {
  // User Management
  getUsers: async ( params?: { page?: number; limit?: number; search?: string; status?: string } ) => {
    const response = await api.get( '/admin/getUsers', { params } );
    return response.data;
  },

  getUserById: async ( id: string ) => {
    const response = await api.get( `/admin/users/${ id }` );
    return response.data;
  },

  updateUser: async ( id: string, userData: Partial<User> ) => {
    const response = await api.post( `/admin/editProfile/${ id }`, userData );
    return response.data;
  },

  deleteUser: async ( data: { userId: string, status: string } ) => {
    const response = await api.post( `/admin/updateUserStatus`, data );
    return response.data;
  },

  toggleUserStatus: async ( data: { userId: string, status: string } ) => {
    const response = await api.post( `/admin/updateUserStatus`, data );
    return response.data;
  },

  // Authentication
  login: async ( credentials: { email: string; password: string } ) => {

    const response = await api.post( '/admin/login', credentials );
    return response.data;
  },

  changePassword: async ( data: {
    currentPassword: string;
    newPassword: string;
  } ) => {
    const response = await api.post( '/admin/changePassword', data );
    return response.data;
  },

  forgotPassword: async ( email: string ) => {
    const response = await api.post( '/admin/sendOtp', { email } );
    return response.data;
  },

  resetPassword: async ( data: {
    email: string;
    otp: string;
    password: string;
  } ) => {
    const response = await api.post( '/admin/setPassword', data );
    return response.data;
  },
  getProfile: async () => {
    try {
      const response = await api.get( '/admin/getProfile' );
      debugLog( 'GetProfile response:', response );
      return response.data;
    } catch ( error ) {
      debugLog( 'GetProfile error:', error );
      throw error;
    }
  },

};

export const notificationService = {
  getNotifications: async () => {
    const response = await api.get( '/admin/getNotifications' );
    return response.data;
  },

  createNotification: async ( data: { title: string; message: string } ) => {
    const response = await api.post( '/admin/createNotification', data );
    return response.data;
  },

  deleteNotification: async ( id: string ) => {
    const response = await api.delete( `/admin/deleteNotification/${ id }` );
    return response.data;
  },

  resendNotification: async ( id: string ) => {
    const response = await api.put( `/admin/resendNotification/${ id }` );
    return response.data;
  }
};

export const containerService = {
  getContainers: async ( params?: { page?: number; limit?: number; search?: string; status?: string } ) => {
    const response = await api.get( '/admin/getContainers', { params } );
    return response.data;
  },

  getContainer: async ( id: string ) => {
    const response = await api.get( `/admin/getContainerById/${ id }` );
    return response.data;
  },

  updateContainer: async ( id: string, data: {
    containerNumber: string;
    containerName: string;
    serialNumber: string;
    location: string;
    address: string;
    latitude: string;
    longitude: string;
    zipCode: string;
    imeiNumber: string;
  } ) => {
    const response = await api.put( `/admin/updateContainer/${ id }`, data );
    return response.data;
  },

  updateContainerStatus: async ( data: { containerId: number; status: string } ) => {
    const response = await api.post( '/admin/updateContainerStatus', data );
    return response.data;
  },

  createContainer: async ( data: {
    containerNumber: string;
    containerName: string;
    imeiNumber: string;
    serialNumber: string;
    address: string;
    latitude: string;
    longitude: string;
    zipCode: string;
  } ) => {
    const response = await api.post( '/admin/addContainer', data );
    return response.data;
  },

  createLocker: async ( data: {
    containerId: number;
    lockerId: string;
    price: string | number | null;
  } ) => {
    const response = await api.post( '/admin/createLocker', {
      containerId: data.containerId,
      lockerId: data.lockerId,
      price: data.price,
    } );
    return response.data;
  },

  updateLockerStatus: async ( data: { lockerId: number; status: string } ) => {
    const response = await api.post( '/admin/updateLocker', data );
    return response.data;
  },
  updateLocker: async ( data: {
    id: string;
    lockerId: string;
    price: string;
    status: string;
  } ) => {

    const response = await api.post( '/admin/updateLocker', {
      id: data.id,
      lockerId: data.lockerId,
      price: data.price,
      status: data.status,
    } );

    return response.data;
  },

};

export const settingsService = {
  createUpdateSettings: async ( settings: {
    minDurationBookLocker: string;
    maxDurationBookLocker: string;
    maxLockerBookingTime: string;
    maxResponseTime: string;
    maxOfferAcceptanceTime: string;
    extendLockerDurarionBefore: string;
    maxOfferSubmissionTime: string;
    maxOfferCollectTime: string;
  } ) => {
    const response = await api.post( '/admin/createOrUpdateSetting', settings );
    return response.data;
  },

  fetchSettings: async () => {
    const response = await api.get( '/admin/fetchSetting' );
    return response.data;
  }
};

export const purchaseService = {
  getUserPurchases: async ( userId: string, params?: { page?: number; limit?: number; search?: string; status?: string } ) => {
    const response = await api.get( `/admin/getUserPurchase/${ userId }`, { params } );
    return response.data;
  }
};

export const salesService = {
  getUserSales: async ( userId: string, params?: { page?: number; limit?: number; search?: string; status?: string; } ) => {
    const response = await api.get( `/admin/getUserSold/${ userId }`, { params } );
    return response.data;
  }
};

export const dashboardService = {
  getDashboardData: async () => {
    const response = await api.get( '/admin/getDashboard' );
    return response.data;
  }
};

export const offersService = {
  getOffers: async ( params?: { page?: number; limit?: number; status?: string; search?: string | null; } ) => {
    const response = await api.get( '/admin/getOffers', { params } );
    return response.data;
  },
  availableSet: async ( lockerId: any, offerId: any ) => {
    const response = await api.put( '/admin/takeItemFromLocker', { lockerId, offerId } );
    return response.data;
  }
};

export const offerDetails = {
  getOfferDetails: async ( id: number ) => {
    const response = await api.get( `/admin/getOfferDetails/${ id }` );
    return response.data;
  },
  supportSystemPickupItemFromLocker: async ( offerId: number, lockerId: any, remark: string | null ) => {
    const response = await api.post( '/admin/supportSystemPickupItem', {
      offerId,
      lockerId,
      remark
    } );
    return response.data;
  },

  sellerTakeItemFromSupportSystem: async ( offerId: number, remark: string | null ) => {
    const response = await api.post( '/admin/sellerTakeItemFromSupportSystem', {
      offerId,
      remark

    } );
    return response.data;
  },

  sellerTakeItemSellerDeclined: async ( offerId: number, remark: string | null, status: string, transactionCode: number ) => {
    const response = await api.post( '/admin/supportSystemReturnItem', {
      offerId,
      remark,
      status,
      transactionCode,


    } );
    return response.data;
  },

  supportrefundTransferAmount: async ( offerId: number, remark: string | null, status: string ) => {
    const response = await api.post( '/admin/refundTransferAmount', {
      offerId,
      remark,
      status

    } );
    return response.data;
  },

}
export const paymentHistory = {
  getPaymentHistory: async ( params?: { page?: number; limit?: number; paymentType?: string | null; status?: string; search?: string | null; } ) => {
    const response = await api.get( '/admin/paymentlist', { params } );
    return response.data;
  },

};

export default api;
